import {Draft, produce} from 'immer';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
// eslint-disable-next-line @typescript-eslint/naming-convention, camelcase
import {unstable_batchedUpdates} from 'react-dom';
import {
  applyNodeChanges,
  Connection,
  Edge,
  Node,
  ReactFlowInstance,
  useOnSelectionChange,
  useStoreApi,
  useUpdateNodeInternals,
} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ActionMethod, ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {composeFlowchartEventMetadata} from 'performance/processProcessesEvents';
import {isFlowchartFeatureEnabled} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/FlowchartFeaturesContext';
import {getIncrementedNodeName} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import {FlowchartConfigMetadataWithRootTaskSchema} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartAdapter';
import {ProcessFlowchartEditorModeFeatureType} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {getRouterNodeIdsBetweenNodes} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {ProcessesPluginBase} from 'processes/graph-builder-processes/platform/ProcessesPluginBase';
import {isNewEdgeValidForFlowchart} from 'processes/modals/utils/TransitionModalValidations';
import {isFlowchartTaskSchema} from 'processes/processes-platform/isFlowchartTaskSchema';
import {
  FlowchartChangeGenerator,
  FlowchartChanges,
  ShouldAddEdgesForNewNodeFn,
  UseFlowchartStateParams,
  UseFlowchartStateReturn,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/FlowchartStateTypes';
import legacyRunModal from 'ui-library/legacyRunModal';
import isFocusSupercededByModal from 'ui-library/modals/isFocusSupercededByModal';
import {ElkEdgeData} from 'ui-library/reactflow/edges/edgeOptions';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import {COMMON_NODE_WIDTH} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import {useFlowchartSidepanelManagerContext} from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelUnsavedChangesContext';
import {
  SidepanelChange,
  SidepanelType,
} from 'ui-library/reactflow/sidepanels/sidepanel-manager/useFlowchartSidepanelManager';
import * as keyboard from 'util/Keyboard';
import {omit} from 'util/omitKey';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import FlowchartRemoveNodeModal from '../../../../../ui-library/reactflow/nodes/modals/FlowchartRemoveNodeModal';
import {isNodeDeletable} from '../adapterUtils';
import {AddNodeOption, getAddNodeOptions} from '../AddNodeDropdown';
import buildInteractiveFlowchartFromStaticData from '../buildInteractiveFlowchartFromStaticData';
import {
  buildControlFlowEdge,
  generateDeterministicEdgeId,
} from '../buildStaticDataFromFlowchartConfigGraphQL';
import doesNodeHaveRightSidepanelContentUtil from '../doesNodeHaveRightSidepanelContent';
import {getEdgeTypeFromHandleId, getFieldInfoFromDataFlowEdgeHandle} from '../flowchartIdentifierUtils';
import {
  countEdgesByType,
  CustomMappingData,
  FlowchartEdge,
  FlowchartStaticEdgeData,
  INTERACTION_FIELDS,
  ProcessFlowchartEdgeType,
  TaskSchemaNodeSectionType,
  TransitionSource,
} from '../processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  isInteractivePlaceholderNode,
  isInteractiveTaskNode,
  isStaticEndNodeData,
  isStaticRouterNodeData,
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
} from '../processFlowchartConfigEditorNodeTypes';
import {
  DeletedNodeContext,
  EdgeRemoveChangeWithDeletedNodeContext,
  findNodeIdsByPTDVClientId,
  FlowchartEdgeChange,
  FlowchartEdgeChangeType,
  FlowchartMetadataOptionsChange,
  FlowchartMetadataOptionsChangeType,
  FlowchartNodeChange,
  FlowchartNodeChangeType,
  FlowchartOptions,
  getRootAdapter,
  TransitionChange,
} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigMetadata} from '../redux/reduceFlowchartConfig';
import {
  addDataFlowEdgesForTransition,
  removeDataFlowEdgesForTransition,
} from '../types/transition/transitionDataFlowEdgeSync';
import {
  buildTransitionIndexByPTDVPair,
  getFieldPairKey,
  getPTDVPairKey,
  getTransitionId,
  parseFieldPairKey,
} from '../types/transition/transitionKeyUtils';
import {
  createTransitionsForNewControlFlowEdge,
  findOrphanedTransitionAndSourcePTDVIds,
  getFieldPairKeyFromDataFlowEdge,
  getOptionalTransitionsForNewEdge,
  getPTDVIdsFromEdge,
  removeFieldPairForDataFlowEdge,
  removeSourcePTDVFromTransition,
  resetCustomMappingsForChangedFields,
  shouldAddEdges,
  singleTransitionStillNeededAfterMergePromotion,
} from '../types/transition/transitionLifecycleUtils';
import {findTaskSchemaNodePair} from '../types/transition/transitionLookupUtils';
import {useTransitionsContext} from '../types/transition/TransitionsContext';
import {FlowchartConfigWithPartialMetadata} from './BaseFlowchartConfigStateManager';
import {centerNodeInVisibleCanvas} from './centerNodeInVisibleCanvas';
import {processEdgeChangesWithMirroring} from './edgeMirroringUtils';
import {getChangedFieldTypeClientIds} from './fieldDefinitionUtils';
import getNodeChangesForNewEdge from './getNodeChangesForNewEdge';
import getRouterConfigChangesAfterEdgeChange from './getRouterConfigChangesAfterEdgeChange';
import {shouldAddEdgesForNewNode} from './shouldAddEdgesForNewNode';
import {shouldBlockDeleteKeyPress} from './shouldBlockDeleteKeyPress';
import {
  DeletedNodeMetadata,
  getExecutionNodeTypeIdentifierForNode,
  trackNodeRemovalAnalytics,
  trackEdgeRemovalAnalytics,
  trackMultiSelectAnalytics,
  trackOptionalTransitionPromotedAnalytics,
} from './useFlowchartStateAnalytics';
import {detectProfilerNodeChanges, logNodeChangeProfilerEvents} from './useFlowchartStateProfiler';
import {useSidepanelDeselection} from './useSidepanelDeselection';

/**
 * Validates edge changes and filters out invalid changes.
 */
function validateEdgeChanges(
  edgeChanges: Array<FlowchartEdgeChange>,
  existingEdges: Array<FlowchartStaticEdgeData>
): Array<FlowchartEdgeChange> {
  return edgeChanges.filter((edgeChange) => {
    // Only validate 'add' changes, others are always valid
    if (edgeChange.type !== FlowchartEdgeChangeType.ADD || !edgeChange.item) {
      return true;
    }

    return isNewEdgeValidForFlowchart(edgeChange.item, existingEdges);
  });
}

export function getEmptyFlowchartChanges(): FlowchartChanges {
  return {
    nodeChanges: [],
    edgeChanges: [],
    transitionChanges: [],
    metadataOptionsChanges: [],
  };
}

function applyFlowchartNodeChanges(
  nodeChanges: Array<FlowchartNodeChange>,
  draft: Draft<FlowchartConfigWithMetadata>,
  setNodePositions: React.Dispatch<React.SetStateAction<Record<string, {x: number; y: number}>>>,
  shouldIncrementNodeNames: boolean = true
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();
  nodeChanges.forEach((change) => {
    switch (change.type) {
      case FlowchartNodeChangeType.ADD:
        if (change.item != null) {
          let newNodeData: StaticFlowchartNodeData = extractStaticData(change.item.data);
          const existingNodeNames = shouldIncrementNodeNames
            ? draft.staticFlowchart.nodesStaticData.map((node) => node.nodeName)
            : undefined;
          if (isStaticRouterNodeData(newNodeData) || isStaticEndNodeData(newNodeData)) {
            const incrementedName = getIncrementedNodeName(newNodeData.nodeName, existingNodeNames);
            newNodeData = {...newNodeData, nodeName: incrementedName};
          } else if (isStaticTaskSchemaNodeData(newNodeData)) {
            const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
              newNodeData.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
            );
            const nodeName = adapter.getNodeName(
              newNodeData.taskDefinitionVersionSpec,
              newNodeData.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
              existingNodeNames
            );
            newNodeData = {...newNodeData, nodeName};
          }
          draft.staticFlowchart.nodesStaticData.push(newNodeData);
          // Set initial position in local state
          setNodePositions((prevPositions) => ({
            ...prevPositions,
            [change.item.id]: change.item.position || {x: 0, y: 0},
          }));
        }
        break;
      case FlowchartNodeChangeType.POSITION:
        if (change.position) {
          setNodePositions((prevPositions) => ({
            ...prevPositions,
            [change.id]: change.position as {x: number; y: number},
          }));
        }
        break;
      case FlowchartNodeChangeType.SELECT: {
        // Only modify the Set if the change would actually change it (prevents unnecessary re-renders)
        const isCurrentlySelected = draft.metadata.interactionState.selectedNodeIds.has(change.id);
        if (change.selected && !isCurrentlySelected) {
          draft.metadata.interactionState.selectedNodeIds.add(change.id);
        } else if (!change.selected && isCurrentlySelected) {
          draft.metadata.interactionState.selectedNodeIds.delete(change.id);
        }
        break;
      }
      case FlowchartNodeChangeType.SELECT_ROW: {
        // Only modify the Set if the change would actually change it (prevents unnecessary re-renders)
        const isCurrentlySelected = draft.metadata.interactionState.selectedRowIds.has(change.rowId);
        if (change.selected && !isCurrentlySelected) {
          draft.metadata.interactionState.selectedRowIds.add(change.rowId);
        } else if (!change.selected && isCurrentlySelected) {
          draft.metadata.interactionState.selectedRowIds.delete(change.rowId);
        }
        break;
      }
      case FlowchartNodeChangeType.REMOVE: {
        const removedNode = draft.staticFlowchart.nodesStaticData.find((node) => node.id === change.id);
        assert(removedNode != null, 'Node does not exist in static data');

        // Capture PTDV ID before removing the node so we can clean up transitions
        const removedPTDVId = isStaticTaskSchemaNodeData(removedNode)
          ? removedNode.taskDefinitionVersionSpec.clientId
          : null;
        const removedNodeWasRouter = isStaticRouterNodeData(removedNode);

        draft.staticFlowchart.nodesStaticData = draft.staticFlowchart.nodesStaticData.filter(
          (node) => node.id !== change.id
        );
        draft.metadata.interactionState.selectedNodeIds.delete(change.id);
        // Clean up local position state
        setNodePositions((prevPositions) => {
          const newPositions = {...prevPositions};
          delete newPositions[change.id];
          return newPositions;
        });

        // Enqueue orphaned edge removals, attaching the deleted node's PTDV ID so
        // that findOrphanedTransitionAndSourcePTDVIds can still resolve the PTDV pair
        // even though the node is no longer in nodesStaticData.
        const orphanedEdges = draft.staticFlowchart.edgesStaticData.filter(
          (edge) => edge.source === change.id || edge.target === change.id
        );
        sideEffects.edgeChanges.push(
          ...buildEdgeRemovalsForDeletedNode(
            change.id,
            orphanedEdges,
            draft.staticFlowchart.nodesStaticData,
            removedPTDVId,
            removedNodeWasRouter
          )
        );
        break;
      }
      case FlowchartNodeChangeType.REPLACE: {
        const nodeToReplace = draft.staticFlowchart.nodesStaticData.find((node) => node.id === change.id);
        if (nodeToReplace) {
          let newNodeData: StaticFlowchartNodeData = extractStaticData(change.data);
          // Node name healing
          if (isStaticTaskSchemaNodeData(nodeToReplace) && newNodeData.nodeName === '') {
            const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
              nodeToReplace.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
            );
            const defaultName = adapter.getNodeName(
              nodeToReplace.taskDefinitionVersionSpec,
              nodeToReplace.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
              draft.staticFlowchart.nodesStaticData.map((node) => node.nodeName)
            );
            newNodeData = {...newNodeData, nodeName: defaultName};
          }
          if (isStaticTaskSchemaNodeData(nodeToReplace) && isStaticTaskSchemaNodeData(newNodeData)) {
            const changedFieldClientIds = getChangedFieldTypeClientIds(
              nodeToReplace.taskDefinitionVersionSpec.inputFieldDefinitions,
              newNodeData.taskDefinitionVersionSpec.inputFieldDefinitions,
              nodeToReplace.taskDefinitionVersionSpec.outputFieldDefinitions,
              newNodeData.taskDefinitionVersionSpec.outputFieldDefinitions
            );
            if (changedFieldClientIds.size > 0) {
              resetCustomMappingsForChangedFields(
                draft.metadata.transitions,
                nodeToReplace.taskDefinitionVersionSpec.clientId,
                changedFieldClientIds
              );
            }
          }
          Object.assign(nodeToReplace, newNodeData);

          // Keep metadata.rootTaskSchema in sync when the root node's fields
          // change.  The node's taskSchema is typed as TaskSchemaSpec but at
          // runtime it's the full ProcessTaskSchemaGraphQL (set by
          // cacheUpdatedTaskSchemaAndUpdateNode)
          if (
            isStaticTaskSchemaNodeData(nodeToReplace) &&
            nodeToReplace.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS &&
            draft.metadata.rootTaskSchema != null
          ) {
            const updatedTaskSchema =
              nodeToReplace.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema;
            // Only sync rootTaskSchema when we have the full GQL schema (indicated by
            // __typename). In the transition modal, nodes are built from bare TaskSchemaSpec
            // without __typename, so we skip the sync — rootTaskSchema is already correct
            // from its original GQL source.
            if ((updatedTaskSchema as {__typename?: string}).__typename === 'ProcessTaskSchemaNode') {
              draft.metadata.rootTaskSchema =
                updatedTaskSchema as ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
            }
          }
        }
        break;
      }
      case FlowchartNodeChangeType.RESET:
        // TODO: Explore if reset changes need to be handled?
        break;
      default:
        assert(false, 'Unexpected node change type', {change});
    }
  });

  return sideEffects;
}

function applyAddEdgeChange(
  edgeItem: Edge,
  draft: Draft<FlowchartConfigWithMetadata>,
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>,
  sideEffects: FlowchartChanges
): void {
  assert(edgeItem.sourceHandle != null, 'Source handle is required');
  assert(edgeItem.targetHandle != null, 'Target handle is required');
  assert(edgeItem.type != null, 'Type is required');
  const newEdge: FlowchartStaticEdgeData = {
    id: edgeItem.id,
    source: edgeItem.source,
    target: edgeItem.target,
    sourceHandle: edgeItem.sourceHandle,
    targetHandle: edgeItem.targetHandle,
    type: edgeItem.type as ProcessFlowchartEdgeType,
  };
  draft.staticFlowchart.edgesStaticData.push(newEdge);

  sideEffects.nodeChanges.push(
    ...getNodeChangesForNewEdge(draft.staticFlowchart, newEdge, draft.metadata.rootNodeStaticData)
  );

  const newTransitions = createTransitionsForNewControlFlowEdge(
    newEdge,
    draft.staticFlowchart.nodesStaticData,
    draft.staticFlowchart.edgesStaticData,
    draft.metadata.transitions
  );
  for (const transition of newTransitions) {
    draft.metadata.transitions[transition.id] = transition;
  }
  if (newEdge.type === ProcessFlowchartEdgeType.CONTROL_FLOW) {
    for (const transition of getOptionalTransitionsForNewEdge(
      newEdge,
      draft.staticFlowchart.nodesStaticData,
      draft.staticFlowchart.edgesStaticData,
      draft.metadata.transitions
    )) {
      sideEffects.transitionChanges.push({type: 'update', id: transition.id, changes: {isPlanned: true}});
    }
  }

  if (newEdge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
    const nodePair = findTaskSchemaNodePair(newEdge, draft.staticFlowchart.nodesStaticData);
    if (nodePair) {
      const sourcePTDVId = nodePair.sourceNode.taskDefinitionVersionSpec.clientId;
      const targetPTDVId = nodePair.targetNode.taskDefinitionVersionSpec.clientId;
      const pairKey = getPTDVPairKey(sourcePTDVId, targetPTDVId);
      // Only mutate planned transitions here. Optional transitions represent
      // ACM/PCM configuration that's independent of the main canvas, so canvas
      // DF edge add/remove should not touch their field pair mappings.
      const transitionId = (ptdvPairToTransitionIdIndex[pairKey] ?? []).find(
        (id) => draft.metadata.transitions[id]?.isPlanned
      );

      if (transitionId != null) {
        const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(newEdge.sourceHandle);
        const targetHandle = getFieldInfoFromDataFlowEdgeHandle(newEdge.targetHandle);
        const fieldPairKey = getFieldPairKey({
          sourcePTDVId,
          sourceSectionType: sourceHandle.sectionType,
          sourceFieldClientId: sourceHandle.fieldClientId,
          targetPTDVId,
          targetSectionType: targetHandle.sectionType,
          targetFieldClientId: targetHandle.fieldClientId,
        });
        // Only initialize the field pair entry if it doesn't already exist in the
        // transition. This preserves custom mapping lookups when data flow edges are
        // re-created via mirroring (e.g., drawing a new control flow edge between
        // nodes that share a PTDV with an existing configured pair).
        const existingTransition = draft.metadata.transitions[transitionId];
        if (
          existingTransition == null ||
          !existingTransition.customMappingDataByFieldPair.hasOwnProperty(fieldPairKey)
        ) {
          sideEffects.transitionChanges.push({
            type: 'update',
            id: transitionId,
            changes: {customMapping: {fieldPairKey, value: null}},
          });
        }
      }
    }
  }
}

/**
 * Checks whether any remaining data flow edge in the flowchart connects the same
 * PTDV pair with the same source/target field handles as the given removed edge.
 */
function hasRemainingDataFlowEdgeForFieldPair(
  removedEdge: FlowchartStaticEdgeData,
  removedEdgePtdvIds: {sourcePTDVId: string; targetPTDVId: string},
  staticFlowchart: {
    edgesStaticData: Array<FlowchartStaticEdgeData>;
    nodesStaticData: Array<StaticFlowchartNodeData>;
  }
): boolean {
  const removedFieldPairKey = getFieldPairKeyFromDataFlowEdge(removedEdge, staticFlowchart.nodesStaticData, {
    deletedSourceNodePTDVId: removedEdgePtdvIds.sourcePTDVId,
    deletedTargetNodePTDVId: removedEdgePtdvIds.targetPTDVId,
  });
  if (removedFieldPairKey == null) {
    return false;
  }

  return staticFlowchart.edgesStaticData.some(
    (edge) => getFieldPairKeyFromDataFlowEdge(edge, staticFlowchart.nodesStaticData) === removedFieldPairKey
  );
}

/**
 * Builds edge removal side effects for all edges connected to a deleted node.
 * When the deleted node is a router, resolves the upstream task's PTDV so that
 * outgoing edges (Router → TaskX) can identify the effective PTDV pair for
 * transition orphan detection.
 */
function buildEdgeRemovalsForDeletedNode(
  deletedNodeId: string,
  orphanedEdges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>,
  removedPTDVId: string | null,
  removedNodeWasRouter: boolean
): Array<EdgeRemoveChangeWithDeletedNodeContext> {
  let routerUpstreamPTDVId: string | undefined;
  if (removedNodeWasRouter) {
    const incomingCFEdge = orphanedEdges.find(
      (e) => e.target === deletedNodeId && e.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    );
    if (incomingCFEdge != null) {
      const upstreamNode = nodes.find((n) => n.id === incomingCFEdge.source);
      if (upstreamNode != null && isStaticTaskSchemaNodeData(upstreamNode)) {
        routerUpstreamPTDVId = upstreamNode.taskDefinitionVersionSpec.clientId;
      }
    }
  }

  return orphanedEdges.map((edge) => {
    const isSourceDeleted = edge.source === deletedNodeId;
    const isTargetDeleted = edge.target === deletedNodeId;
    return {
      type: 'remove' as const,
      id: edge.id,
      deletedNodeContext: {
        deletedSourceNodePTDVId: isSourceDeleted
          ? removedNodeWasRouter
            ? routerUpstreamPTDVId
            : (removedPTDVId ?? undefined)
          : undefined,
        deletedTargetNodePTDVId: isTargetDeleted ? (removedPTDVId ?? undefined) : undefined,
        deletedNodeWasRouter: removedNodeWasRouter || undefined,
      },
    };
  });
}

function applyRemoveEdgeChange(
  edgeId: string,
  draft: Draft<FlowchartConfigWithMetadata>,
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>,
  sideEffects: FlowchartChanges,
  deletedNodeContext?: DeletedNodeContext
): void {
  const removedEdge = draft.staticFlowchart.edgesStaticData.find((edge) => edge.id === edgeId);
  draft.staticFlowchart.edgesStaticData = draft.staticFlowchart.edgesStaticData.filter(
    (edge) => edge.id !== edgeId
  );
  draft.metadata.interactionState.selectedEdgeIds.delete(edgeId);

  sideEffects.nodeChanges.push(
    ...getRouterConfigChangesAfterEdgeChange(draft.staticFlowchart, FlowchartEdgeChangeType.REMOVE, edgeId)
  );

  if (removedEdge != null) {
    const {orphanedTransitionIds, orphanedSourcePTDV} = findOrphanedTransitionAndSourcePTDVIds(
      removedEdge,
      draft.staticFlowchart.edgesStaticData,
      draft.staticFlowchart.nodesStaticData,
      ptdvPairToTransitionIdIndex,
      draft.metadata.transitions,
      deletedNodeContext
    );
    for (const orphanedId of orphanedTransitionIds) {
      delete draft.metadata.transitions[orphanedId];
    }
    for (const {transitionId, sourcePTDVId} of orphanedSourcePTDV) {
      const transition = draft.metadata.transitions[transitionId];
      removeSourcePTDVFromTransition(draft.metadata.transitions, transitionId, sourcePTDVId);
      if (transition != null && transition.id !== transitionId) {
        // Transition was re-keyed — update the stale index so subsequent
        // edge removals in the same batch can still find it.
        for (const remainingSourceId of transition.sourcePTDVIds) {
          const pairKey = getPTDVPairKey(remainingSourceId, transition.targetPTDVId);
          const ids = ptdvPairToTransitionIdIndex[pairKey];
          if (ids != null) {
            const idx = ids.indexOf(transitionId);
            if (idx !== -1) {
              ids[idx] = transition.id;
            }
          }
        }
      }
    }

    if (removedEdge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      const ptdvIds = getPTDVIdsFromEdge(
        removedEdge,
        draft.staticFlowchart.nodesStaticData,
        deletedNodeContext
      );
      if (ptdvIds) {
        if (!hasRemainingDataFlowEdgeForFieldPair(removedEdge, ptdvIds, draft.staticFlowchart)) {
          const pairKey = getPTDVPairKey(ptdvIds.sourcePTDVId, ptdvIds.targetPTDVId);
          for (const transitionId of ptdvPairToTransitionIdIndex[pairKey] ?? []) {
            const transition = draft.metadata.transitions[transitionId];
            // Optional transitions represent ACM/PCM configuration independent
            // of the main canvas, so canvas DF edge removal must not clear
            // their field pair mappings.
            if (transition != null && transition.isPlanned) {
              const updatedCustomMappingData = removeFieldPairForDataFlowEdge(
                removedEdge,
                draft.staticFlowchart.nodesStaticData,
                transition,
                deletedNodeContext
              );
              if (updatedCustomMappingData != null) {
                transition.customMappingDataByFieldPair = updatedCustomMappingData;
              }
            }
          }
        }
      }
    }
  }
}

function applyFlowchartEdgeChanges(
  edgeChanges: Array<FlowchartEdgeChange>,
  draft: Draft<FlowchartConfigWithMetadata>,
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();

  const allEdgeChanges = processEdgeChangesWithMirroring(edgeChanges, draft);
  const validatedEdgeChanges = validateEdgeChanges(allEdgeChanges, draft.staticFlowchart.edgesStaticData);

  // Process ADDs before REMOVEs so that when edges are restructured, new edges
  // exist when the old edge's removal triggers orphan detection.
  // If a future ADD needs to check an updated list of edges including removals,
  // this ordering may need to be revisited.
  const sortedEdgeChanges = [...validatedEdgeChanges].sort((a, b) => {
    const order = {
      [FlowchartEdgeChangeType.ADD]: 0,
      [FlowchartEdgeChangeType.SELECT]: 1,
      [FlowchartEdgeChangeType.LABEL]: 1,
      [FlowchartEdgeChangeType.RESET]: 1,
      [FlowchartEdgeChangeType.REMOVE]: 2,
    };
    return (order[a.type] ?? 1) - (order[b.type] ?? 1);
  });

  sortedEdgeChanges.forEach((change) => {
    switch (change.type) {
      case FlowchartEdgeChangeType.ADD:
        if (change.item != null) {
          applyAddEdgeChange(change.item, draft, ptdvPairToTransitionIdIndex, sideEffects);
        }
        break;
      case FlowchartEdgeChangeType.SELECT:
        if (change.selected) {
          draft.metadata.interactionState.selectedEdgeIds.add(change.id);
        } else {
          draft.metadata.interactionState.selectedEdgeIds.delete(change.id);
        }
        break;
      case FlowchartEdgeChangeType.REMOVE:
        applyRemoveEdgeChange(
          change.id,
          draft,
          ptdvPairToTransitionIdIndex,
          sideEffects,
          change.deletedNodeContext
        );
        break;
      case FlowchartEdgeChangeType.LABEL: {
        draft.metadata.interactionState.edgeLabelsByEdgeId[change.id] = change.label;
        const targetEdge = draft.staticFlowchart.edgesStaticData.find((edge) => edge.id === change.id);
        assert(targetEdge != null, 'Target edge should not be null');
        targetEdge.label = change.label;
        break;
      }
      case FlowchartEdgeChangeType.RESET:
        // TODO: Explore if reset changes need to be handled?
        break;
      default:
        assert(false, 'Unexpected edge change type', {change});
    }
  });

  return sideEffects;
}

/**
 * Clears router expressions between two nodes.
 * When the transition source changes, router configurations that depend on it become invalid
 * and need to be cleared. The default function spec is not cleared because it does not have
 * an expression associated with it.
 */
function clearRouterExpressionsBetweenNodes(
  draft: Draft<FlowchartConfigWithMetadata>,
  sourceNodeId: string,
  targetNodeId: string
): Array<FlowchartNodeChange> {
  const nodeChanges: Array<FlowchartNodeChange> = [];
  const routerNodeIds = getRouterNodeIdsBetweenNodes(
    {
      nodes: draft.staticFlowchart.nodesStaticData.map((node) => ({id: node.id, data: node})),
      edges: draft.staticFlowchart.edgesStaticData,
    },
    sourceNodeId,
    targetNodeId
  );

  for (const routerNodeId of routerNodeIds) {
    const routerNode = draft.staticFlowchart.nodesStaticData.find((node) => node.id === routerNodeId)!;
    assert(isStaticRouterNodeData(routerNode), 'Expected router node');
    const newData = produce(routerNode, (draftRouterNode) => {
      draftRouterNode.routerConfig.functionSpecs = draftRouterNode.routerConfig.functionSpecs.map((spec) => ({
        ...spec,
        expression: null,
        isConfigured: false,
      }));
    });
    nodeChanges.push({
      id: routerNodeId,
      type: FlowchartNodeChangeType.REPLACE,
      data: newData,
    });
  }

  return nodeChanges;
}

function transformCustomMappingForTransitionSourceChange({
  customMapping,
  previousTransitionSource,
  newTransitionSource,
  sourceSectionType,
}: {
  customMapping: CustomMappingData;
  previousTransitionSource: TransitionSource;
  newTransitionSource: TransitionSource;
  sourceSectionType: TaskSchemaNodeSectionType;
}): CustomMappingData {
  if (customMapping.lookupConfig == null) {
    return customMapping;
  }

  // PROCESS_TASK navigates from the output schema to the task schema. It's only needed when:
  //   - The transition source is OUTPUTS (from_schema = output schema), AND
  //   - The source field is from the INPUTS section (field lives on the task schema)
  // For OUTPUTS section fields, the SCHEMA_FIELD already references a field on the output schema,
  // so adding PROCESS_TASK would incorrectly navigate to the task schema.
  // MAP_FROM_START uses FLOWCHART_PROCESS_TASK for navigation, independent of transition source.

  if (sourceSectionType === TaskSchemaNodeSectionType.INPUTS) {
    const lookupSteps = [...customMapping.lookupConfig.lookupSteps];

    if (
      previousTransitionSource === TransitionSource.INPUTS &&
      newTransitionSource === TransitionSource.OUTPUTS
    ) {
      if (lookupSteps.length === 0 || lookupSteps[0].type !== LookupStepType.PROCESS_TASK) {
        lookupSteps.unshift({type: LookupStepType.PROCESS_TASK});
      }
    } else if (
      previousTransitionSource === TransitionSource.OUTPUTS &&
      newTransitionSource === TransitionSource.INPUTS
    ) {
      if (lookupSteps.length > 0 && lookupSteps[0].type === LookupStepType.PROCESS_TASK) {
        lookupSteps.shift();
      }
    }

    return {
      ...customMapping,
      lookupConfig: {
        ...customMapping.lookupConfig,
        lookupSteps,
      },
    };
  }

  if (sourceSectionType === TaskSchemaNodeSectionType.OUTPUTS) {
    // OUTPUTS fields should never have a leading PROCESS_TASK step. Strip it defensively
    // in case legacy configs were created with one under old logic.
    const lookupSteps = [...customMapping.lookupConfig.lookupSteps];
    if (lookupSteps.length > 0 && lookupSteps[0].type === LookupStepType.PROCESS_TASK) {
      lookupSteps.shift();
    }
    if (lookupSteps.length !== customMapping.lookupConfig.lookupSteps.length) {
      return {
        ...customMapping,
        lookupConfig: {
          ...customMapping.lookupConfig,
          lookupSteps,
        },
      };
    }
    return customMapping;
  }

  // MAP_FROM_START: navigation is handled by FLOWCHART_PROCESS_TASK, unaffected by transition source.
  return customMapping;
}

function applyMetadataOptionsChanges(
  metadataOptionsChanges: Array<FlowchartMetadataOptionsChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();
  metadataOptionsChanges.forEach((change) => {
    switch (change.type) {
      case FlowchartMetadataOptionsChangeType.UPDATE:
        draft.metadata.options = {...draft.metadata.options, ...change.options};
        break;
      default:
        assert(false, 'Unexpected options change type', {change});
    }
  });

  return sideEffects;
}

interface TransitionSourceHealingResult {
  nodeChanges: Array<FlowchartNodeChange>;
}

/**
 * Applies autohealing side effects when a transition source changes.
 * This clears router expressions between affected nodes.
 */
function healTransitionSourceChange(
  draft: Draft<FlowchartConfigWithMetadata>,
  sourcePTDVId: string,
  targetPTDVId: string
): TransitionSourceHealingResult {
  const nodeChanges: Array<FlowchartNodeChange> = [];

  const sourceNodeIds = findNodeIdsByPTDVClientId(draft.staticFlowchart.nodesStaticData, sourcePTDVId);
  const targetNodeIds = findNodeIdsByPTDVClientId(draft.staticFlowchart.nodesStaticData, targetPTDVId);

  for (const sourceNodeId of sourceNodeIds) {
    for (const targetNodeId of targetNodeIds) {
      nodeChanges.push(...clearRouterExpressionsBetweenNodes(draft, sourceNodeId, targetNodeId));
    }
  }

  return {nodeChanges};
}

function applyTransitionChanges(
  transitionChanges: Array<TransitionChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();
  transitionChanges.forEach((change) => {
    switch (change.type) {
      case 'add':
        draft.metadata.transitions[change.transition.id] = change.transition;
        if (change.transition.isPlanned) {
          const addedEdges = addDataFlowEdgesForTransition(draft.staticFlowchart, change.transition);
          for (const edge of addedEdges) {
            sideEffects.nodeChanges.push(
              ...getNodeChangesForNewEdge(draft.staticFlowchart, edge, draft.metadata.rootNodeStaticData)
            );
          }
        }
        break;
      case 'remove': {
        const existing = draft.metadata.transitions[change.id];
        if (existing != null && existing.isPlanned) {
          const removedEdgeIds = removeDataFlowEdgesForTransition(draft.staticFlowchart, existing);
          for (const edgeId of removedEdgeIds) {
            sideEffects.edgeChanges.push({type: FlowchartEdgeChangeType.REMOVE, id: edgeId});
          }
        }
        delete draft.metadata.transitions[change.id];
        break;
      }
      case 'update': {
        const existing = draft.metadata.transitions[change.id];
        if (existing == null) {
          break;
        }

        if (change.changes.transitionSource != null) {
          const previousValue =
            existing.transitionSourcesBySourcePTDVId[change.changes.transitionSource.sourcePTDVId];
          existing.transitionSourcesBySourcePTDVId[change.changes.transitionSource.sourcePTDVId] =
            change.changes.transitionSource.value;

          if (change.changes.transitionSource.value !== previousValue) {
            const healingResult = healTransitionSourceChange(
              draft,
              change.changes.transitionSource.sourcePTDVId,
              existing.targetPTDVId
            );
            sideEffects.nodeChanges.push(...healingResult.nodeChanges);

            if (previousValue != null) {
              const transformedMappings: Record<string, CustomMappingData | null> = {};
              for (const [fieldPairKey, customMapping] of Object.entries(
                existing.customMappingDataByFieldPair
              )) {
                const parsedKey = parseFieldPairKey(fieldPairKey);
                transformedMappings[fieldPairKey] =
                  customMapping != null && parsedKey != null
                    ? transformCustomMappingForTransitionSourceChange({
                        customMapping,
                        previousTransitionSource: previousValue,
                        newTransitionSource: change.changes.transitionSource.value,
                        sourceSectionType: parsedKey.sourceSectionType,
                      })
                    : customMapping;
              }
              existing.customMappingDataByFieldPair = transformedMappings;
            }
          }
        }
        if (change.changes.customMapping != null) {
          const {fieldPairKey, value: customMappingValue} = change.changes.customMapping;
          existing.customMappingDataByFieldPair[fieldPairKey] = customMappingValue;
        }
        if (change.changes.isPlanned != null && change.changes.isPlanned !== existing.isPlanned) {
          const wasPlanned = existing.isPlanned;
          const isPromoting = !wasPlanned && change.changes.isPlanned;
          existing.isPlanned = change.changes.isPlanned;
          if (wasPlanned) {
            const removedEdgeIds = removeDataFlowEdgesForTransition(draft.staticFlowchart, existing);
            for (const edgeId of removedEdgeIds) {
              sideEffects.edgeChanges.push({type: FlowchartEdgeChangeType.REMOVE, id: edgeId});
            }
          } else {
            const addedEdges = addDataFlowEdgesForTransition(draft.staticFlowchart, existing);
            for (const edge of addedEdges) {
              sideEffects.nodeChanges.push(
                ...getNodeChangesForNewEdge(draft.staticFlowchart, edge, draft.metadata.rootNodeStaticData)
              );
            }
            if (isPromoting && existing.sourcePTDVIds.length > 1) {
              for (const sourcePTDVId of existing.sourcePTDVIds) {
                const singleTransitionId = getTransitionId([sourcePTDVId], existing.targetPTDVId);
                if (
                  draft.metadata.transitions[singleTransitionId] != null &&
                  !singleTransitionStillNeededAfterMergePromotion({
                    sourcePTDVId,
                    mergingSourcePTDVIds: existing.sourcePTDVIds,
                    targetPTDVId: existing.targetPTDVId,
                    nodes: draft.staticFlowchart.nodesStaticData,
                    edges: draft.staticFlowchart.edgesStaticData,
                  })
                ) {
                  sideEffects.transitionChanges.push({type: 'remove', id: singleTransitionId});
                }
              }
            }
          }
        }
        break;
      }
      default:
        assert(false, 'Unexpected transition change type', {change});
    }
  });

  return sideEffects;
}

/**
 * Detects which parts of the flowchart state have changed and returns only the changed parts.
 *
 * Uses Immer's structural sharing: unchanged parts keep their original references, while changed parts get new references.
 * This enables fast reference equality checks to identify what actually changed.
 *
 * We return only changed parts (not the entire state) so reduceFlowchartConfig can merge these changes with the latest server state.
 */
function detectChangedParts(
  original: FlowchartConfigWithMetadata,
  updated: FlowchartConfigWithMetadata
): FlowchartConfigWithPartialMetadata | null {
  // Early short-circuit: if both references are the same, nothing changed
  // (This only happens if produce() was never called)
  if (original === updated) {
    return null;
  }

  const staticFlowchartChanged = updated.staticFlowchart !== original.staticFlowchart;

  const interactionStateChanged = updated.metadata.interactionState !== original.metadata.interactionState;
  const transitionsChanged = updated.metadata.transitions !== original.metadata.transitions;
  const metadataOptionsChanged = updated.metadata.options !== original.metadata.options;
  const rootTaskSchemaChanged = updated.metadata.rootTaskSchema !== original.metadata.rootTaskSchema;

  const anyMetadataChanged =
    interactionStateChanged || transitionsChanged || metadataOptionsChanged || rootTaskSchemaChanged;

  // Early short-circuit: if nothing actually changed, return null
  if (!staticFlowchartChanged && !anyMetadataChanged) {
    return null;
  }

  // Build the result object based on what actually changed
  const buildMetadataChanges = (): Partial<FlowchartConfigMetadata> => {
    const metadataChanges: Partial<FlowchartConfigMetadata> = {};

    if (interactionStateChanged) {
      metadataChanges.interactionState = updated.metadata.interactionState;
    }

    if (transitionsChanged) {
      metadataChanges.transitions = updated.metadata.transitions;
    }

    if (metadataOptionsChanged) {
      metadataChanges.options = updated.metadata.options;
    }

    if (rootTaskSchemaChanged) {
      metadataChanges.rootTaskSchema = updated.metadata.rootTaskSchema;
    }

    return metadataChanges;
  };

  if (staticFlowchartChanged && anyMetadataChanged) {
    return {
      staticFlowchart: updated.staticFlowchart,
      flowchartConfigMetadata: buildMetadataChanges(),
    };
  } else if (staticFlowchartChanged) {
    return {
      staticFlowchart: updated.staticFlowchart,
    };
  } else {
    return {
      flowchartConfigMetadata: buildMetadataChanges(),
    };
  }
}

/**
 * Extracts the static data from the data object of a flowchart node.
 * @param nodeData The data object on a FlowchartNode.
 * @returns The static data of the node.
 */
export function extractStaticData(nodeData: FlowchartNode['data']): StaticFlowchartNodeData {
  return omit(
    nodeData,
    ...(INTERACTION_FIELDS as unknown as Array<keyof FlowchartNode['data']>)
  ) as StaticFlowchartNodeData;
}

export function extractStaticEdgeData(edge: FlowchartEdge): FlowchartStaticEdgeData {
  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
    type: edge.type,
    label: edge.label,
  };
}

function combineFlowchartChanges(
  sideEffects: FlowchartChanges,
  additionalChanges: Partial<FlowchartChanges>
): FlowchartChanges {
  return {
    nodeChanges: [...sideEffects.nodeChanges, ...(additionalChanges.nodeChanges ?? [])],
    edgeChanges: [...sideEffects.edgeChanges, ...(additionalChanges.edgeChanges ?? [])],
    transitionChanges: [...sideEffects.transitionChanges, ...(additionalChanges.transitionChanges ?? [])],
    metadataOptionsChanges: [
      ...sideEffects.metadataOptionsChanges,
      ...(additionalChanges.metadataOptionsChanges ?? []),
    ],
  };
}

/**
 * Core logic for applying flowchart changes to static data, metadata, and interactive state.
 */
export function applyFlowchartChangesCore(
  currentState: FlowchartConfigWithMetadata,
  changes: Partial<FlowchartChanges>,
  setNodePositions: React.Dispatch<React.SetStateAction<Record<string, {x: number; y: number}>>>,
  plugin?: ProcessesPluginBase,
  ptdvPairToTransitionIdIndex?: Record<string, Array<string>>,
  shouldIncrementNodeNames: boolean = true
): FlowchartConfigWithPartialMetadata | null {
  const {nodeChanges = [], edgeChanges = [], transitionChanges = [], metadataOptionsChanges = []} = changes;

  let currSideEffects: FlowchartChanges = {
    nodeChanges,
    edgeChanges,
    transitionChanges,
    metadataOptionsChanges,
  };

  const getDoesSideEffectsHaveChanges = (sideEffects: FlowchartChanges): boolean =>
    Object.values(sideEffects).some((changeArray) => changeArray.length > 0);

  if (!getDoesSideEffectsHaveChanges(currSideEffects)) {
    return null;
  }

  const transitionIndex =
    ptdvPairToTransitionIdIndex ?? buildTransitionIndexByPTDVPair(currentState.metadata.transitions);

  const updatedFlowchartAndMetadata = produce(currentState, (draft) => {
    let cumulativeChanges: FlowchartChanges = getEmptyFlowchartChanges();

    do {
      cumulativeChanges = combineFlowchartChanges(cumulativeChanges, currSideEffects);

      // Apply all changes and their cascading side effects until the state is stable.
      // This ensures nodes and edges (and other state) are fully consistent before the healer runs.
      let pendingSideEffects = currSideEffects;

      while (getDoesSideEffectsHaveChanges(pendingSideEffects)) {
        const nodeSideEffects = applyFlowchartNodeChanges(
          pendingSideEffects.nodeChanges,
          draft,
          setNodePositions,
          shouldIncrementNodeNames
        );
        const edgeSideEffects = applyFlowchartEdgeChanges(
          pendingSideEffects.edgeChanges,
          draft,
          transitionIndex
        );
        const transitionSideEffects = applyTransitionChanges(pendingSideEffects.transitionChanges, draft);
        const metadataOptionsSideEffects = applyMetadataOptionsChanges(
          pendingSideEffects.metadataOptionsChanges,
          draft
        );

        const allSideEffects = [
          nodeSideEffects,
          edgeSideEffects,
          transitionSideEffects,
          metadataOptionsSideEffects,
        ];
        pendingSideEffects = allSideEffects.reduce(
          (acc, curr) => ({
            nodeChanges: [...acc.nodeChanges, ...curr.nodeChanges],
            edgeChanges: [...acc.edgeChanges, ...curr.edgeChanges],
            transitionChanges: [...acc.transitionChanges, ...curr.transitionChanges],
            metadataOptionsChanges: [...acc.metadataOptionsChanges, ...curr.metadataOptionsChanges],
          }),
          getEmptyFlowchartChanges()
        );

        cumulativeChanges = combineFlowchartChanges(cumulativeChanges, pendingSideEffects);
      }

      // State is now fully consistent. Collect healing side effects from the validator.
      if (plugin != null) {
        const healingChanges = plugin.flowchartValidator.healFlowchart(cumulativeChanges)(draft);
        currSideEffects = combineFlowchartChanges(getEmptyFlowchartChanges(), healingChanges);
      } else {
        currSideEffects = getEmptyFlowchartChanges();
      }
    } while (getDoesSideEffectsHaveChanges(currSideEffects));
  });

  return detectChangedParts(currentState, updatedFlowchartAndMetadata);
}

/**
 * Contains the core flowchart state management logic for the horizontal flowchart.
 *
 * Main features:
 * - Apply changes to the flowchart and/or metadata
 * - Handle right sidepanel state management
 * - Handle reactflow actions related to nodes + edges
 *
 * Core concepts:
 * - Minimal flowchart representations to actively track:
 *      We should rarely, if ever, need to mutate or interact with the interactive nodes/edges directly,
 *      which gets sent to reactflow for rendering. Instead, we change the static flowchart, metadata and interactive
 *      state trackers as needed, and the interactive flowchart is rebuilt from those sources of truth automatically.
 * - All change management is funneled through the applyFlowchartChanges function:
 *      This allows for centralizing side effects and autohealing logic related to flowchart changes.
 *

 * @returns The flowchart state management logic.
 */
const useFlowchartState = (params: UseFlowchartStateParams): UseFlowchartStateReturn => {
  const {
    staticFlowchart,
    flowchartConfigMetadata,
    flowchartConfigId,
    validationErrors,
    setFlowchartAndMetadata,
    addNodeParams,
    flowchartFeatures,
    reactFlowInstance,
    reactFlowWrapper,
    leftSidepanelRef,
    rightSidepanelRef,
    rightPanelSize,
  } = params;

  const {flowchartTypeName} = flowchartConfigMetadata;
  const plugin = PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType(flowchartTypeName);
  const {ptdvPairToTransitionIdIndex} = useTransitionsContext();
  const shouldIncrementNodeNames = isFlowchartFeatureEnabled(
    flowchartFeatures,
    ProcessFlowchartEditorModeFeatureType.SHOW_NODE_NAME_INCREMENT
  );

  const locked = ReactFlowLockedContext.useContext() ?? false;
  const allowStartEndNodeDeletion = isFlowchartFeatureEnabled(
    flowchartFeatures,
    ProcessFlowchartEditorModeFeatureType.ALLOW_START_END_NODE_DELETION
  );
  const reactFlowStore = useStoreApi();

  const getDefaultNodeDimensions = useCallback((): {width: number; height: number} => {
    const fallbackNodeHeight = COMMON_NODE_WIDTH * 2;
    return {
      width: COMMON_NODE_WIDTH,
      height: fallbackNodeHeight,
    };
  }, []);

  const getMeasuredDimensionsFromStore = useCallback(
    (nodeId: string): {width: number; height: number} => {
      const {nodeInternals} = reactFlowStore.getState();
      const nodeInternal = nodeInternals.get(nodeId);

      const width = nodeInternal?.width;
      const height = nodeInternal?.height;
      if (width != null && height != null && width > 0 && height > 0) {
        return {width, height};
      }

      return getDefaultNodeDimensions();
    },
    [reactFlowStore, getDefaultNodeDimensions]
  );

  const [interactiveNodes, setInteractiveNodes] = useState<Array<FlowchartNode>>([]);
  const [interactiveEdges, setInteractiveEdges] = useState<Array<FlowchartEdge>>([]);
  // By node ID
  const [nodePositions, setNodePositions] = useState<Record<string, {x: number; y: number}>>({});

  // Ref to track current node positions for use in rebuilds during drag operations
  // This allows us to preserve drag positions without triggering full flowchart rebuilds
  const currentNodePositionsRef = useRef<Record<string, {x: number; y: number}>>({});

  const interactiveNodesRef = useRef<Array<FlowchartNode>>(interactiveNodes);
  interactiveNodesRef.current = interactiveNodes;

  const sidepanelManager = useFlowchartSidepanelManagerContext();
  const sidepanelState = sidepanelManager.getEffectiveState();
  const {openSidepanelType} = sidepanelState;
  const sidepanelStateRef = useRef(sidepanelState);
  sidepanelStateRef.current = sidepanelState;
  const isLeftSidepanelOpen = useMemo(
    () => openSidepanelType === SidepanelType.FLOWCHART_SETTINGS,
    [openSidepanelType]
  );

  const [_sourceNodeAndNewNodeBeingAdded, setSourceNodeAndNewNodeBeingAdded] = useState<
    [string, FlowchartNode] | null
  >(null);

  const [placeholderNodeIdBeingAdded, setPlaceholderNodeBeingAdded] = useState<string | null>(null);

  // Track node IDs that need their handle positions refreshed in ReactFlow.
  // When node sections change (e.g., field reordering), handles move to new positions.
  const nodeIdsToRefreshRef = useRef<Set<string>>(new Set());
  // Tracks section row clientIds per node to detect handle ID changes across renders.
  // When section clientIds change (e.g., after save assigns persisted parameter IDs),
  // the corresponding ReactFlow Handle DOM elements get new IDs. ReactFlow's internal
  // handle position cache must be refreshed via updateNodeInternals for edges to render
  // correctly against the new handles.
  const prevSectionClientIdsRef = useRef<Record<string, string>>({});
  const updateNodeInternals = useUpdateNodeInternals();

  const applyFlowchartChanges = useCallback(
    (
      changeGenerator: FlowchartChangeGenerator,
      saveImmediately: boolean = false,
      skipSettingDirtyState: boolean = false
    ): Promise<FlowchartConfigMetadata | null> =>
      setFlowchartAndMetadata(
        (currentState) => {
          if (!currentState) {
            return null;
          }

          const changes = changeGenerator(currentState);

          // When locked, allow selection changes so the sidepanel can open in read-only mode,
          // but block all other changes (add/remove/replace nodes, edge changes, metadata changes)
          if (locked) {
            const onlySelection =
              (changes.nodeChanges ?? []).every((c) => c.type === FlowchartNodeChangeType.SELECT) &&
              (changes.edgeChanges ?? []).every((c) => c.type === FlowchartEdgeChangeType.SELECT) &&
              !(changes.transitionChanges?.length ?? 0);
            if (!onlySelection) {
              return null;
            }
          }

          // When nodes are replaced (e.g., field reordering), handle positions might change.
          // Track these nodes. Their edges will need to be recalculated
          const replacedNodeIds = (changes.nodeChanges ?? [])
            .filter((c) => c.type === FlowchartNodeChangeType.REPLACE)
            .map((c) => c.id);

          if (replacedNodeIds.length > 0) {
            replacedNodeIds.forEach((nodeId) => nodeIdsToRefreshRef.current.add(nodeId));
          }

          const profilerFlags = detectProfilerNodeChanges(
            changes.nodeChanges ?? [],
            currentState.staticFlowchart.nodesStaticData
          );
          logNodeChangeProfilerEvents(profilerFlags, 'start');

          const result = applyFlowchartChangesCore(
            currentState,
            changes,
            setNodePositions,
            plugin,
            ptdvPairToTransitionIdIndex,
            shouldIncrementNodeNames
          );

          const flowchartForProfilerMetadata = result?.staticFlowchart ?? currentState.staticFlowchart;

          logNodeChangeProfilerEvents(
            profilerFlags,
            'end',
            composeFlowchartEventMetadata({
              flowchartType: flowchartTypeName,
              numNodes: flowchartForProfilerMetadata.nodesStaticData.length,
              ...countEdgesByType(flowchartForProfilerMetadata.edgesStaticData),
              flowchartConfigId,
            })
          );

          return result;
        },
        saveImmediately,
        skipSettingDirtyState
      ),
    [
      setFlowchartAndMetadata,
      locked,
      plugin,
      ptdvPairToTransitionIdIndex,
      shouldIncrementNodeNames,
      flowchartTypeName,
      flowchartConfigId,
    ]
  );

  // Should only be used for reactflow-specific node changes
  // When the FlowchartConfigWithMetadata can't be used to generate changes
  const onReactFlowNodeChangesOnly = useCallback(
    (nodeChanges: Array<FlowchartNodeChange>) => {
      const positionChanges = nodeChanges.filter((c) => c.type === FlowchartNodeChangeType.POSITION);
      // We ignore dimension changes (height and width of a node). Autolayout is the only code that relies on this as of this writing.
      // However, dimensions don't seem to propagate from reactflow when the flowchart is locked. Instead,
      // we expect autolayout code to grab the dimensions from the node internals on its own.
      const otherChanges = nodeChanges.filter(
        (c) => c.type !== FlowchartNodeChangeType.POSITION && c.type !== FlowchartNodeChangeType.DIMENSIONS
      );

      if (positionChanges.length > 0) {
        // Handle position changes locally using ReactFlow's applyNodeChanges
        // This avoids triggering Redux updates and full flowchart rebuilds during dragging
        setInteractiveNodes((nodes) => {
          const updatedNodes = applyNodeChanges(positionChanges, nodes);
          updatedNodes.forEach((node: Node) => {
            currentNodePositionsRef.current[node.id] = node.position;
          });
          return updatedNodes;
        });

        // Also update local position state
        positionChanges.forEach((change) => {
          if (change.position) {
            setNodePositions((prevPositions) => ({
              ...prevPositions,
              [change.id]: change.position as {x: number; y: number},
            }));
          }
        });
      }

      if (otherChanges.length > 0) {
        applyFlowchartChanges((flowchartConfig) => ({nodeChanges: otherChanges, edgeChanges: []}));
      }
    },
    [applyFlowchartChanges]
  );

  // Should only be used for reactflow-specific edge changes
  // When the FlowchartConfigWithMetadata can't be used to generate changes
  const onReactFlowEdgeChangesOnly = useCallback(
    (edgeChanges: Array<FlowchartEdgeChange>) => {
      applyFlowchartChanges((flowchartConfig) => ({nodeChanges: [], edgeChanges}));
    },
    [applyFlowchartChanges]
  );

  const deleteNodesWithConfirmation = useCallback(
    async (nodesToDelete: Array<FlowchartNode>, edgesToDelete: Array<FlowchartEdge> = []): Promise<void> => {
      const deletableNodes = nodesToDelete.filter((node) =>
        isNodeDeletable(node, {allowStartEndNodeDeletion})
      );

      if (deletableNodes.length === 0) {
        return;
      }

      const nodeNames = deletableNodes.map((node) => node.data.nodeName);

      const edgeChanges = edgesToDelete.map((edge) => ({
        type: 'remove' as const,
        id: edge.id,
      }));

      const nodeChanges = deletableNodes.map((node) => ({
        type: 'remove' as const,
        id: node.id,
      }));

      const rootAdapter = getRootAdapter(plugin);
      assert(
        rootAdapter != null && flowchartConfigMetadata.rootTaskSchema != null,
        'Expected rootAdapter and rootTaskSchema to be defined'
      );

      const confirmationContent = rootAdapter.getRemoveNodeConfirmationContent(
        nodeNames.length === 1
          ? {
              isMultiNodeRemoval: false as const,
              nodeName: nodeNames[0],
              metadata: flowchartConfigMetadata as FlowchartConfigMetadataWithRootTaskSchema,
            }
          : {
              isMultiNodeRemoval: true as const,
              metadata: flowchartConfigMetadata as FlowchartConfigMetadataWithRootTaskSchema,
            }
      );

      const deletedNodesMetadata: Array<DeletedNodeMetadata> = deletableNodes.map((node) => {
        const isTaskNode = isInteractiveTaskNode(node);
        return {
          nodeName: node.data.nodeName,
          nodeType: node.data.nodeType,
          executionType: isTaskNode
            ? node.data.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
            : null,
          taskSchemaId: isTaskNode ? node.data.taskSchemaId : null,
        };
      });

      const taskSchemaIdsOnCanvas = staticFlowchart.nodesStaticData
        .filter((node) => isStaticTaskSchemaNodeData(node))
        .map((node) => node.taskSchemaId);

      const previouslyFocusedElement = document.activeElement as HTMLElement | null;
      const result = await legacyRunModal(FlowchartRemoveNodeModal, {
        nodeNames,
        title: confirmationContent.title,
        subtitle: confirmationContent.subtitle,
        onRequestRemove: () => {
          trackNodeRemovalAnalytics(deletedNodesMetadata, taskSchemaIdsOnCanvas, flowchartTypeName);

          sidepanelManager.forceClose();

          if (edgeChanges.length > 0) {
            onReactFlowEdgeChangesOnly(edgeChanges);
          }

          onReactFlowNodeChangesOnly(nodeChanges);
        },
      });
      if (result == null) {
        // If canceling deletion, keep node focused to allow delete key again
        previouslyFocusedElement?.focus();
      }
    },
    [
      allowStartEndNodeDeletion,
      onReactFlowEdgeChangesOnly,
      onReactFlowNodeChangesOnly,
      sidepanelManager,
      plugin,
      flowchartConfigMetadata,
      staticFlowchart.nodesStaticData,
      flowchartTypeName,
    ]
  );

  const onNodeDelete = useCallback(
    (nodeId: string): void => {
      if (locked) {
        return;
      }

      const node = reactFlowInstance?.getNode(nodeId) as FlowchartNode | undefined;
      if (node == null) {
        return;
      }

      deleteNodesWithConfirmation([node]);
    },
    [locked, reactFlowInstance, deleteNodesWithConfirmation]
  );

  // Reverts node + field selection back to a specific node and field
  const revertNodeSelection = useCallback(
    (nodeId: string, fieldId: string | null) => {
      applyFlowchartChanges((flowchartConfigWithMetadata) => {
        const nodeChanges: Array<FlowchartNodeChange> = [];

        // Deselect any currently selected nodes
        const currentlySelectedNodeIds =
          flowchartConfigWithMetadata.metadata.interactionState.selectedNodeIds;
        currentlySelectedNodeIds.forEach((selectedId: string) => {
          if (selectedId !== nodeId) {
            nodeChanges.push({
              type: FlowchartNodeChangeType.SELECT,
              id: selectedId,
              selected: false,
            });
          }
        });

        nodeChanges.push({
          type: FlowchartNodeChangeType.SELECT,
          id: nodeId,
          selected: true,
        });

        return {nodeChanges};
      }).then(() => {
        setInteractiveNodes((currentNodes: Array<FlowchartNode>) =>
          currentNodes.map((node: FlowchartNode) => ({
            ...node,
            selected: node.id === nodeId,
          }))
        );
      });
    },
    [applyFlowchartChanges, setInteractiveNodes]
  );

  const openSidepanelAndSelectNode = useCallback(
    async (nodeId: string, fieldId: string | null = null, rowId: string | null = null): Promise<void> => {
      const {nodeId: currentNodeId, fieldId: currentFieldId} = sidepanelManager.getEffectiveState();

      const success = await sidepanelManager.changeSidepanel({
        sidepanelChange: {
          sidepanel: SidepanelType.NODE_SETTINGS,
          nodeId,
          fieldId,
        },
      });

      if (!success) {
        // User chose "Keep Editing" - revert selection back to current node
        if (currentNodeId != null) {
          revertNodeSelection(currentNodeId, currentFieldId);
        }
        return;
      }

      applyFlowchartChanges((flowchartConfigWithMetadata) => {
        const nodeChanges: Array<FlowchartNodeChange> = [];

        // Deselect any currently selected nodes or rows
        flowchartConfigWithMetadata.metadata.interactionState.selectedNodeIds.forEach(
          (selectedId: string) => {
            if (selectedId !== nodeId) {
              nodeChanges.push({
                type: FlowchartNodeChangeType.SELECT,
                id: selectedId,
                selected: false,
              });
            }
          }
        );
        flowchartConfigWithMetadata.metadata.interactionState.selectedRowIds.forEach(
          (selectedRowId: string) => {
            if (selectedRowId !== rowId) {
              nodeChanges.push({
                type: FlowchartNodeChangeType.SELECT_ROW,
                id: nodeId,
                rowId: selectedRowId,
                selected: false,
              });
            }
          }
        );

        // Select the target node
        nodeChanges.push({
          type: FlowchartNodeChangeType.SELECT,
          id: nodeId,
          selected: true,
        });
        if (rowId != null) {
          nodeChanges.push({
            type: FlowchartNodeChangeType.SELECT_ROW,
            id: nodeId,
            rowId,
            selected: true,
          });
        }

        return {nodeChanges};
      });

      // Center the node in the visible canvas after sidepanel opens
      if (reactFlowInstance && reactFlowWrapper?.current) {
        // Use setTimeout to ensure DOM has updated after sidepanel opens
        setTimeout(() => {
          const nodePosition = nodePositions[nodeId];
          const dimensions = getMeasuredDimensionsFromStore(nodeId);
          if (nodePosition) {
            centerNodeInVisibleCanvas({
              reactFlowInstance,
              reactFlowWrapper,
              leftSidepanelRef: leftSidepanelRef ?? {current: null},
              rightSidepanelRef: rightSidepanelRef ?? {current: null},
              nodePosition,
              nodeWidth: dimensions.width,
              nodeHeight: dimensions.height,
            });
          }
        }, 0);
      }
    },
    [
      sidepanelManager,
      revertNodeSelection,
      applyFlowchartChanges,
      reactFlowInstance,
      reactFlowWrapper,
      leftSidepanelRef,
      rightSidepanelRef,
      nodePositions,
      getMeasuredDimensionsFromStore,
    ]
  );

  const doesNodeHaveRightSidepanelContent = useCallback(
    (nodeId: string): boolean => {
      const node = interactiveNodesRef.current.find((n) => n.id === nodeId);
      if (node == null) {
        return false;
      }
      return doesNodeHaveRightSidepanelContentUtil(
        node.data,
        params.flowchartEditorConfigurator.platformFlowchartFeatures
      );
    },
    [params.flowchartEditorConfigurator]
  );

  const openSidepanelForNode = useCallback(
    (nodeId: string): void => {
      if (!doesNodeHaveRightSidepanelContent(nodeId)) {
        return;
      }
      const node = interactiveNodesRef.current.find((n) => n.id === nodeId);
      if (node == null) {
        return;
      }
      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.SELECTED_NODE_IN_CANVAS, {
        actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
        nodeType: getExecutionNodeTypeIdentifierForNode(node),
        flowchartType: flowchartTypeName,
      });
      openSidepanelAndSelectNode(nodeId, null, null);
    },
    [openSidepanelAndSelectNode, doesNodeHaveRightSidepanelContent, flowchartTypeName]
  );

  const openSidepanelForNodeWithField = useCallback(
    (nodeId: string, fieldId: string, rowId: string): void => {
      if (!doesNodeHaveRightSidepanelContent(nodeId)) {
        return;
      }
      openSidepanelAndSelectNode(nodeId, fieldId, rowId);
    },
    [openSidepanelAndSelectNode, doesNodeHaveRightSidepanelContent]
  );

  const getAddNodeOptionsForRightHandle = useCallback(
    (sourceNodeId: string, reactFlowInstance: ReactFlowInstance): Array<AddNodeOption> | null =>
      getAddNodeOptions({
        flowchartEditorConfigurator: addNodeParams.flowchartEditorConfigurator,
        sourceNodeId,
        reactFlowInstance,
        singletonTaskSchemasByName: addNodeParams.singletonTaskSchemasByName,
        canAddExistingTaskSchema: addNodeParams.taskDefinitionsForSidepanel.some(
          (def) => !isFlowchartTaskSchema(def.latestVersion.processTaskSchema)
        ),
        rootTaskSchema: addNodeParams.rootTaskSchema,
        openSidepanelForNode,
        openSidepanelForNodeWithField,
        onNodeChangesOnly: (nodeChangeGenerator) => {
          applyFlowchartChanges((flowchartConfig) => ({
            nodeChanges: nodeChangeGenerator(flowchartConfig),
            edgeChanges: [],
          }));
        },
        setSourceNodeAndNewNodeBeingAdded: ([sourceNodeId, newNode]: [string, FlowchartNode]) => {
          analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.NODE_ADDED_FROM_RIGHT_HANDLE_CLICK, {
            actionMethod: ActionMethod.BUTTON_CLICK,
            actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
            nodeType: getExecutionNodeTypeIdentifierForNode(newNode),
            flowchartType: flowchartTypeName,
          });
          setSourceNodeAndNewNodeBeingAdded([sourceNodeId, newNode]);
        },
        didInitialFlowchartHaveChainedRouters: addNodeParams.didInitialFlowchartHaveChainedRouters,
      }),
    [
      addNodeParams,
      openSidepanelForNode,
      openSidepanelForNodeWithField,
      applyFlowchartChanges,
      flowchartTypeName,
    ]
  );

  const interactiveFlowchartOptions: FlowchartOptions = useMemo(
    () => ({
      onNodeHeaderClick: openSidepanelForNode,
      onNodeDelete,
      onFieldClick: openSidepanelForNodeWithField,
      onUpdateNodeData: (nodeChangeGenerator, saveImmediately, skipSettingDirtyState) =>
        applyFlowchartChanges(
          (flowchartConfig) => ({
            nodeChanges: nodeChangeGenerator(flowchartConfig),
          }),
          saveImmediately,
          skipSettingDirtyState
        ),
      getAddNodeOptionsForRightHandle,
      validationErrors,
      existingNodeNames: staticFlowchart.nodesStaticData.map((node) => node.nodeName),
      platformFlowchartFeatures: params.flowchartEditorConfigurator.platformFlowchartFeatures,
    }),
    [
      openSidepanelForNode,
      onNodeDelete,
      openSidepanelForNodeWithField,
      getAddNodeOptionsForRightHandle,
      validationErrors,
      applyFlowchartChanges,
      staticFlowchart.nodesStaticData,
      params.flowchartEditorConfigurator.platformFlowchartFeatures,
    ]
  );

  const [elkEdgeDataByEdgeId, setElkEdgeDataByEdgeId] = useState({});
  useEffect(() => {
    if (!staticFlowchart) {
      return;
    }

    // Use current positions from ref to preserve drag positions
    // Falls back to local state for nodes that don't exist yet (e.g., newly added)
    const positionsWithFallback = {
      ...nodePositions,
      ...currentNodePositionsRef.current,
    };

    const {nodes, edges} = buildInteractiveFlowchartFromStaticData({
      staticNodesData: staticFlowchart.nodesStaticData,
      staticEdgesData: staticFlowchart.edgesStaticData,
      options: interactiveFlowchartOptions,
      nodePositionsByNodeId: positionsWithFallback,
      selectedNodeIds: flowchartConfigMetadata.interactionState.selectedNodeIds,
      selectedRowIds: flowchartConfigMetadata.interactionState.selectedRowIds,
      selectedEdgeIds: flowchartConfigMetadata.interactionState.selectedEdgeIds,
      elkEdgeDataByEdgeId,
      validationErrors,
    });

    nodes.forEach((node) => {
      currentNodePositionsRef.current[node.id] = node.position;
    });

    setInteractiveNodes(nodes);
    setInteractiveEdges(edges);
    // Intentionally exclude nodePositions to avoid rebuild on every drag.
    // When staticFlowchart changes (e.g. node added), this effect runs and reads nodePositions for new nodes.
    // But we don't add it to deps because position-only changes shouldn't trigger rebuilds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    staticFlowchart,
    flowchartConfigMetadata.interactionState.selectedNodeIds,
    flowchartConfigMetadata.interactionState.selectedRowIds,
    flowchartConfigMetadata.interactionState.selectedEdgeIds,
    interactiveFlowchartOptions,
    elkEdgeDataByEdgeId,
    validationErrors,
  ]);

  // After nodes are rendered with new handle positions, tell ReactFlow to update its
  // internal cache via updateNodeInternals. This triggers ReactFlow to re-measure handle
  // positions from the DOM.
  //
  // Handles needing refresh come from two sources:
  // 1. nodeIdsToRefreshRef: populated by REPLACE node changes (e.g., field reordering)
  // 2. Section clientId changes: detected here by comparing current vs previous render.
  //    This covers the save-complete path where mergeIdsIntoPowProcedureFlowchartConfig
  //    updates section clientIds with persisted parameter IDs, changing handle element IDs
  //    without going through applyFlowchartChanges.
  useEffect(() => {
    const nodeIdsToRefresh = new Set(nodeIdsToRefreshRef.current);
    nodeIdsToRefreshRef.current.clear();

    // Detect nodes whose section row clientIds changed since the last render.
    // Section row clientIds are used as part of ReactFlow Handle element IDs
    // (via makeDataFlowEdgeHandle), so a change means new handle DOM elements
    // that ReactFlow doesn't know about yet.
    for (const node of interactiveNodes) {
      if (isStaticTaskSchemaNodeData(node.data)) {
        const handleKey = node.data.sections.flatMap((s) => s.sectionRows.map((r) => r.clientId)).join('|');
        const prevKey = prevSectionClientIdsRef.current[node.id];
        // Skip initial render (prevKey == null) — only refresh on actual changes.
        if (prevKey != null && prevKey !== handleKey) {
          nodeIdsToRefresh.add(node.id);
        }
        prevSectionClientIdsRef.current[node.id] = handleKey;
      }
    }

    if (nodeIdsToRefresh.size > 0) {
      // Double requestAnimationFrame ensures we wait for:
      // 1. First frame: React commits DOM changes for interactiveNodes update
      // 2. Second frame: Node components render with new handle positions
      // Only then can updateNodeInternals measure the correct handle positions.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          nodeIdsToRefresh.forEach((nodeId) => {
            updateNodeInternals(nodeId);
          });
        });
      });
    }
  }, [interactiveNodes, updateNodeInternals]);

  // Handle adding nodes from right handle
  useEffect(() => {
    if (_sourceNodeAndNewNodeBeingAdded == null) {
      return;
    }
    const [sourceNodeId, newNode] = _sourceNodeAndNewNodeBeingAdded;
    // Clear the state immediately to prevent the effect from running again with the same value
    setSourceNodeAndNewNodeBeingAdded(null);

    const isPlaceholder = isInteractivePlaceholderNode(newNode);
    const edge = buildControlFlowEdge({
      sourceNodeId,
      targetNodeId: newNode.id,
    });

    const sidepanelChange: SidepanelChange = isPlaceholder
      ? {
          sidepanel: SidepanelType.NODE_SETTINGS,
          nodeId: newNode.id,
        }
      : {sidepanel: null};

    sidepanelManager.changeSidepanel({
      sidepanelChange,
      onBeforeSidepanelChanged: async () => {
        await applyFlowchartChanges(
          (flowchartDraftState) => ({
            nodeChanges: [
              {
                type: 'add',
                item: {
                  ...newNode,
                  data: {
                    ...newNode.data,
                    getAddNodeOptionsForRightHandle,
                  },
                },
              },
            ],
            edgeChanges: [
              {
                type: 'add',
                item: edge,
              },
            ],
          }),
          false,
          isPlaceholder
        );
        if (isPlaceholder) {
          setPlaceholderNodeBeingAdded(newNode.id);
        }
        setSourceNodeAndNewNodeBeingAdded(null);
      },
    });
  }, [
    _sourceNodeAndNewNodeBeingAdded,
    setSourceNodeAndNewNodeBeingAdded,
    applyFlowchartChanges,
    getAddNodeOptionsForRightHandle,
    sidepanelManager,
    setPlaceholderNodeBeingAdded,
  ]);

  // Handle opening sidepanel for placeholder nodes
  useEffect(() => {
    if (placeholderNodeIdBeingAdded != null) {
      // Check if the node actually exists in interactiveNodes
      const nodeExists = interactiveNodes.some((node) => node.id === placeholderNodeIdBeingAdded);

      if (!nodeExists) {
        // Node hasn't been added to interactiveNodes yet, wait for next render
        return;
      }

      // Select the placeholder node
      applyFlowchartChanges((flowchartDraftState) => ({
        nodeChanges: [
          {
            type: 'select',
            id: placeholderNodeIdBeingAdded,
            selected: true,
          },
        ],
      }));
      // Open the sidepanel after a brief delay to ensure the getNodeById ref is updated
      setTimeout(() => {
        openSidepanelForNode(placeholderNodeIdBeingAdded);
      }, 0);
      setPlaceholderNodeBeingAdded(null);
    }
  }, [
    placeholderNodeIdBeingAdded,
    openSidepanelForNode,
    setPlaceholderNodeBeingAdded,
    applyFlowchartChanges,
    interactiveNodes,
  ]);

  useSidepanelDeselection({
    applyFlowchartChanges,
  });

  const shouldAddEdgesForNewNodeCallback = useCallback<ShouldAddEdgesForNewNodeFn>(
    (edges, newNodeStaticData) =>
      shouldAddEdgesForNewNode({
        edges,
        newNodeStaticData,
        staticFlowchart,
        ptdvPairToTransitionIdIndex,
        transitions: flowchartConfigMetadata.transitions,
        flowchartTypeName,
      }),
    [staticFlowchart, ptdvPairToTransitionIdIndex, flowchartConfigMetadata.transitions, flowchartTypeName]
  );

  const onConnect = useCallback(
    async (connection: Connection): Promise<void> => {
      if (
        connection.source != null &&
        connection.target != null &&
        connection.sourceHandle != null &&
        connection.targetHandle != null
      ) {
        const {source, target, sourceHandle, targetHandle} = connection;

        const sourceHandleType = getEdgeTypeFromHandleId(sourceHandle);
        const targetHandleType = getEdgeTypeFromHandleId(targetHandle);

        const edgeType =
          sourceHandleType === targetHandleType ? sourceHandleType : ProcessFlowchartEdgeType.CONTROL_FLOW;

        analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.EDGE_DRAWN, {
          actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
          edgeType,
          flowchartType: flowchartTypeName,
        });

        const newStaticEdge: FlowchartStaticEdgeData = {
          id:
            edgeType === ProcessFlowchartEdgeType.CONTROL_FLOW
              ? generateDeterministicEdgeId(source, target)
              : uuidv4(),
          source,
          target,
          sourceHandle,
          targetHandle,
          type: edgeType,
        };

        if (
          await shouldAddEdges({
            edges: [newStaticEdge],
            staticFlowchart,
            ptdvPairToTransitionIdIndex,
            transitions: flowchartConfigMetadata.transitions,
            flowchartTypeName,
          })
        ) {
          trackOptionalTransitionPromotedAnalytics(
            getOptionalTransitionsForNewEdge(
              newStaticEdge,
              staticFlowchart.nodesStaticData,
              [...staticFlowchart.edgesStaticData, newStaticEdge],
              flowchartConfigMetadata.transitions
            ),
            flowchartTypeName
          );
          applyFlowchartChanges(() => ({
            edgeChanges: [{type: FlowchartEdgeChangeType.ADD, item: newStaticEdge}],
          }));
        }
      }
    },
    [
      applyFlowchartChanges,
      flowchartTypeName,
      staticFlowchart,
      ptdvPairToTransitionIdIndex,
      flowchartConfigMetadata.transitions,
    ]
  );

  const setNodesAndEdgesViaChangesForLayout = useCallback(
    (newNodes: Array<Node>, newEdges: Array<Edge>): void => {
      // Use unstable_batchedUpdates to ensure both state updates happen in a single render cycle.
      // NOTE: This is not necessary in React 18
      // Without this, updating nodes and edges separately can cause an interstitial rebuild
      // to fire between the two updates
      // Note: unstable_batchedUpdates is actually quite stable. https://stackoverflow.com/a/48610973
      unstable_batchedUpdates(() => {
        // Update node positions
        const nodeChanges: Array<FlowchartNodeChange> = newNodes.map((node) => ({
          type: FlowchartNodeChangeType.POSITION,
          id: node.id,
          position: node.position,
        }));
        onReactFlowNodeChangesOnly(nodeChanges);

        // Update edge layout data
        const newElkEdgeDataByEdgeId = newEdges.reduce(
          (acc, e) => {
            acc[e.id] = e.data.elkEdgeData;
            return acc;
          },
          {} as Record<string, ElkEdgeData>
        );
        setElkEdgeDataByEdgeId(newElkEdgeDataByEdgeId);
      });
    },
    [setElkEdgeDataByEdgeId, onReactFlowNodeChangesOnly]
  );

  const handleDeleteKeyPress = useCallback(
    (event: KeyboardEvent): void => {
      if (
        locked ||
        !keyboard.isADeleteKey(event) ||
        isFocusSupercededByModal(reactFlowWrapper?.current ?? undefined)
      ) {
        return;
      }

      if (
        shouldBlockDeleteKeyPress({
          target: event.target as HTMLElement,
          reactFlowWrapper: reactFlowWrapper?.current,
          leftSidepanel: leftSidepanelRef?.current,
          rightSidepanel: rightSidepanelRef?.current,
          openSidepanelType: sidepanelStateRef.current.openSidepanelType,
          rightPanelSize,
        })
      ) {
        return;
      }

      const selectedEdges = interactiveEdges.filter((edge) => edge.selected);
      const selectedNodes = interactiveNodes.filter((node) => node.selected);

      if (selectedEdges.length === 0 && selectedNodes.length === 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      // If only edges are selected, delete them immediately without confirmation
      if (selectedEdges.length > 0 && selectedNodes.length === 0) {
        // Build metadata for each deleted edge (for analytics)
        trackEdgeRemovalAnalytics(
          selectedEdges.map((edge) => edge.type),
          flowchartTypeName
        );

        const edgeChanges = selectedEdges.map((edge) => ({
          type: 'remove' as const,
          id: edge.id,
        }));
        onReactFlowEdgeChangesOnly(edgeChanges);
        return;
      }

      // If nodes are selected (with or without edges), show confirmation modal
      if (selectedNodes.length > 0) {
        deleteNodesWithConfirmation(selectedNodes, selectedEdges);
      }
    },
    [
      locked,
      reactFlowWrapper,
      leftSidepanelRef,
      rightSidepanelRef,
      rightPanelSize,
      interactiveNodes,
      interactiveEdges,
      onReactFlowEdgeChangesOnly,
      deleteNodesWithConfirmation,
      flowchartTypeName,
    ]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleDeleteKeyPress);
    return () => {
      document.removeEventListener('keydown', handleDeleteKeyPress);
    };
  }, [handleDeleteKeyPress]);

  // Track multi-select analytics using React Flow's onSelectionChange hook
  // Debounce to avoid firing multiple times during rapid selection changes (e.g., shift-clicking)
  const multiSelectDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSelectionChange = useCallback(
    ({nodes, edges}: {nodes: Array<FlowchartNode>; edges: Array<FlowchartEdge>}) => {
      if (multiSelectDebounceRef.current) {
        clearTimeout(multiSelectDebounceRef.current);
      }
      multiSelectDebounceRef.current = setTimeout(() => {
        trackMultiSelectAnalytics(nodes, edges, flowchartTypeName);
        multiSelectDebounceRef.current = null;
      }, 1000);
    },
    [flowchartTypeName]
  );
  useOnSelectionChange({onChange: onSelectionChange});

  return {
    // State
    interactiveNodes,
    interactiveEdges,
    setInteractiveNodes,
    // Core functions
    applyFlowchartChanges,
    onReactFlowNodeChangesOnly,
    onReactFlowEdgeChangesOnly,
    onConnect,
    shouldAddEdgesForNewNode: shouldAddEdgesForNewNodeCallback,
    setNodesAndEdgesViaChangesForLayout,
    // Sidepanel state
    isLeftSidepanelOpen,
    // Node addition state
    interactiveFlowchartOptions,
    setPlaceholderNodeBeingAdded,
    // Node dimensions
    getMeasuredDimensionsFromStore,
  };
};

export default useFlowchartState;
